import React from 'react';
import { CaretRight, WhatsappLogo, Envelope } from 'phosphor-react';
import { Conversation } from '../types/api';
import { Switch } from './Switch';
import { OrderBadge } from './messages/OrderBadge';

interface ChatTileProps {
  conversation: Conversation;
  onClick?: () => void;
  onModeToggle?: (conversationId: number, isManual: boolean) => void;
  onManualModeRequest?: (conversationId: number) => void;
  onOrderBadgeClick?: (orderId: number) => void;
  className?: string;
}

export const ChatTile: React.FC<ChatTileProps> = ({
  conversation,
  onClick,
  onModeToggle,
  onManualModeRequest,
  onOrderBadgeClick,
  className = ''
}) => {
  // Parse context if it's a string
  const parseContext = (context: any): any => {
    if (!context) return null;
    if (typeof context === 'string') {
      try {
        return JSON.parse(context);
      } catch {
        return null;
      }
    }
    return context;
  };

  const parsedContext = parseContext(conversation.context);
  
  // Get customer data
  const customerName = conversation.customerName; // Nombre de la persona
  const phoneNumber = conversation.customerPhone;
  const company = parsedContext?.company; // Nombre de la empresa
  const isEmailChannel = conversation.channel === 'email';

  // Determine display name (priority: company > customerName > phone/email)
  const displayName = company || customerName || (isEmailChannel ? phoneNumber : phoneNumber) || 'Cliente desconocido';

  // Show phone/email as secondary info (always show if available and not already shown as main name)
  const contactInfo = isEmailChannel ? phoneNumber : phoneNumber;
  const showContact = contactInfo && displayName !== contactInfo;

  // Show person name if company is the main display name
  const showPersonName = company && customerName;

  // Format last message timestamp
  const formatTimestamp = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `Hace ${diffMins}m`;
    if (diffMins < 1440) return `Hace ${Math.floor(diffMins / 60)}h`;
    return date.toLocaleDateString('es-CL', { day: 'numeric', month: 'short' });
  };

  const lastMessageText = conversation.lastMessage || 'Sin mensajes';
  const timestamp = formatTimestamp(conversation.lastMessageAt || conversation.lastActiveAt);
  const hasUnread = conversation.unreadCount && conversation.unreadCount > 0;
  const showUnreadCount = conversation.unreadCount ? conversation.unreadCount > 0 : false;
  const isManualMode = parsedContext?.manualMode === true;
  const isAiMode = !isManualMode; // Invertido: si manualMode es true, AI está desactivado

  const handleSwitchChange = (checked: boolean) => {
    // checked = true significa IA activo (manualMode = false)
    // checked = false significa modo manual activo (manualMode = true)
    
    // Si se está apagando (cambiando de AI a manual), mostrar bottom sheet
    if (!checked && isAiMode && onManualModeRequest) {
      onManualModeRequest(conversation.id);
      return;
    }
    
    // Si se está activando (cambiando de manual a AI), proceder directamente
    if (onModeToggle) {
      onModeToggle(conversation.id, !checked);
    }
  };

  const handleSwitchClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevenir que se abra la conversación
  };

  return (
    <div
      className={`border-b border-border flex items-start px-3 py-3 cursor-pointer hover:bg-black/5 transition-colors duration-base ease-smooth w-full min-w-0 max-w-full ${className}`}
      data-name="chat tile"
      onClick={onClick}
    >
      <div className="flex gap-3 items-start w-full min-w-0 max-w-full">
        {/* Content */}
        <div className="flex-1 flex flex-col gap-1 min-w-0">
          {/* Top row: company/name and timestamp */}
          <div className="flex items-center justify-between gap-2 w-full min-w-0">
            <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
              {hasUnread && (
                <div className="bg-[#5555f1] rounded-full shrink-0 w-2 h-2 flex-shrink-0" />
              )}
              {showUnreadCount && (
                <span className="font-sans font-medium text-sm text-text-subtle">
                  {conversation.unreadCount}
                </span>
              )}
              <p className="font-sans font-medium text-base text-text tracking-[-0.16px] truncate min-w-0 flex-shrink">
                {displayName}
              </p>
              {isManualMode && (
                <span className="bg-amber-100 px-2 py-0.5 rounded-full shrink-0 flex-shrink-0">
                  <p className="font-sans font-medium text-xs text-amber-800 tracking-[-0.12px] whitespace-nowrap">
                    Desactivado
                  </p>
                </span>
              )}
            </div>
            {timestamp && (
              <p className="font-sans font-medium text-xs text-text-subtle tracking-[-0.12px] shrink-0 whitespace-nowrap flex-shrink-0">
                {timestamp}
              </p>
            )}
          </div>

          {/* Additional info: phone number/email and person name */}
          <div className="flex flex-col gap-0.5">
            {showContact && (
              <div className="flex items-center gap-2 min-w-0">
                <p className="font-sans font-medium text-sm text-text-subtle tracking-[-0.14px] truncate">
                  {contactInfo}
                </p>
                {/* Channel Badge inline */}
                {conversation.channel === 'email' && (
                  <span className="bg-blue-100 px-1.5 py-0.5 rounded-full shrink-0 flex-shrink-0 flex items-center gap-0.5">
                    <Envelope size={10} className="text-blue-700" weight="bold" />
                    <p className="font-sans font-medium text-xs text-blue-700 tracking-[-0.12px] whitespace-nowrap">
                      correo
                    </p>
                  </span>
                )}
                {(conversation.channel === 'whatsapp' || !conversation.channel) && (
                  <span className="bg-green-100 px-1.5 py-0.5 rounded-full shrink-0 flex-shrink-0 flex items-center gap-0.5">
                    <WhatsappLogo size={10} className="text-green-700" weight="bold" />
                    <p className="font-sans font-medium text-xs text-green-700 tracking-[-0.12px] whitespace-nowrap">
                      wa
                    </p>
                  </span>
                )}
                {conversation.orderId && (
                  <OrderBadge
                    orderId={conversation.orderId}
                    status={conversation.orderStatus}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onOrderBadgeClick) {
                        onOrderBadgeClick(conversation.orderId!);
                      }
                    }}
                    size="sm"
                  />
                )}
              </div>
            )}
            {/* Show person name if company is shown as main display name */}
            {showPersonName && (
              <p className="font-sans font-medium text-sm text-text-subtle tracking-[-0.14px] truncate">
                {customerName}
              </p>
            )}
          </div>

          {/* Last message preview */}
          <p className="font-sans font-normal text-sm text-text-subtle tracking-[-0.14px] line-clamp-2">
            {lastMessageText}
          </p>
        </div>

        {/* Right controls: Switch + Arrow */}
        <div className="flex flex-col items-center justify-between gap-1 shrink-0 self-stretch">
          <div className="flex items-center gap-1 scale-75 -mr-2" onClick={handleSwitchClick}>
            <Switch
              checked={isAiMode}
              onCheckedChange={handleSwitchChange}
              aria-label={isAiMode ? 'IA activo' : 'Modo manual activo'}
            />
            <span className={`font-sans font-normal text-md transition-opacity duration-base ease-smooth ${
              isAiMode ? 'text-foreground opacity-100' : 'text-muted-foreground opacity-50'
            }`}>
              Auto
            </span>
          </div>
          <CaretRight size={16} weight="bold" color="#2a2a2a" style={{ width: '16px', height: '16px', display: 'block', flexShrink: 0 }} />
        </div>
      </div>
    </div>
  );
};

export default ChatTile;

